import { ChangeEvent, FC, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { Card } from 'components'
import { useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import { SearchFields } from 'pages/Supplier/AttachDocuments/ThirdPartyAdministration/components/Search/components'
import { useForm } from 'react-hook-form'
import {
  ParamFaleConoscoResponseDto,
  TalkWithUsServices
} from 'services/NGT/Parameterization'
import TalkToUsService, {
  ITalkToUsRequestDto,
  ITalkToUsResponseDto
} from 'services/NGT/TalkToUs'
import { SPACING, COLORS, OLDYUP as YUP } from 'utils'

import FilterTalkToUs from './components/FilterTalkToUs'
import TableTalkToUs from './components/TableTalkToUs'
import * as yup from 'yup'

interface ITalkToUsSearch {
  handleAnswerForm: (idFaleConosco: number) => void
}

const DEFAULT_PARAMS_TALK_TO_US: ITalkToUsRequestDto = {
  dtEnvioFim: null,
  dtEnvioInicio: null,
  flLiberado: null,
  idMotivo: 0,
  nmUsuario: '',
  txEmail: '',
  pageNumber: 1,
  pageSize: 15,
}

export interface ITalkToUsSearchProps {
  idMotivo: number
  idUsuario: number
  dtEnvioInicio: Date
  dtEnvioFim: Date
  nmUsuario: string
  txEmail: string
  flLiberado: boolean
}

const SCHEMA = yup.object({
  dtEnvioInicio: yup.date().nullable().notRequired().typeError('Digite uma data válida.'),
  dtEnvioFim: yup.date().nullable().notRequired().typeError('Digite uma data válida.').min(yup.ref('dtEnvioInicio'), 'Data envio fim deve ser maior que data envio inicio.'),
  flLiberado: yup.bool().notRequired().nullable(),
  idMotivo: yup.number(),
  nmUsuario: yup.string().nullable().notRequired(),
  txEmail: yup.string().nullable().notRequired(),
})

const TalkToUsSearch: FC<ITalkToUsSearch> = ({ handleAnswerForm }) => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()

  const [parametrizationTalkToUs, setParametrizationTalkToUs] = useState<
    ParamFaleConoscoResponseDto[]
  >([])
  const [page, setPage] = useState<number>(1)
  const [pageCount, setPageCount] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [changeEffect, toggleChangeEffect] = useToggle()

  const [talkToUsData, setTalkToUsData] = useState<ITalkToUsResponseDto[]>([])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<ITalkToUsSearchProps>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      dtEnvioFim: null,
      dtEnvioInicio: null,
      idMotivo: 0,
      flLiberado: null
    }
  })

  usePersistForm('gst_talk_to_us_search', {
    setValue,
    watch
  })

  const dtEnvioInicio = watch('dtEnvioInicio')
  const dtEnvioFim = watch('dtEnvioFim')

  const areDatesValid = (): boolean => {
    if (
      (moment(dtEnvioInicio).isValid() &&
        !moment(dtEnvioFim).isValid()) ||
      (!moment(dtEnvioInicio).isValid() &&
        moment(dtEnvioFim).isValid())
    ) {
      return false
    }
    return (
      moment(dtEnvioInicio || moment()).isValid() &&
      moment(dtEnvioInicio || moment()).isValid() &&
      (dtEnvioFim && dtEnvioFim
        ? moment(dtEnvioInicio).isSameOrBefore(
          dtEnvioFim
        )
        : true)
    )
  }

  const handleOnClear = () => {
    reset()
    toggleChangeEffect()
    handleSearch(watch())
  }

  const handleSeachParametrizacao = () => {
    setLoading('loading')

    TalkWithUsServices()
      .getAllAutocomplete()
      .then(({ data }) => {
        if (data) {
          setParametrizationTalkToUs(data)
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError

        showModal({
          hideButtonCancel: true,
          message:
            axiosError.message ||
            'Não foi possivel obter as parametrizações de fale conosco',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleSearch = (form: ITalkToUsSearchProps = {} as ITalkToUsSearchProps) => {
    setLoading('loading')

    const requestData: ITalkToUsRequestDto = {
      idMotivo: form.idMotivo ?? 0,
      flLiberado: form.flLiberado ?? null,
      ...(form.dtEnvioInicio && {
        dtEnvioInicio: moment(form.dtEnvioInicio).toISOString()
      }),
      ...(form.dtEnvioFim && {
        dtEnvioFim: moment(form.dtEnvioFim).toISOString()
      }),
      ...(form.nmUsuario && {
        nmUsuario: form.nmUsuario.trim()
      }),
      ...(form.txEmail && {
        txEmail: form.txEmail.trim()
      }),
      pageNumber: page,
      pageSize: rowsPerPage
    }

    TalkToUsService()
      .getAllPaginated(requestData)
      .then(({ data }) => {
        if (data) {
          setTalkToUsData(data.items)
          setPageCount(data.pageCount)
        }
      })
      .catch((error) => {
        const axisoError = error as AxiosError
        showModal({
          hideButtonCancel: true,
          message:
            axisoError.message ||
            'Não foi possivel obter as mensagens de fale conosco',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  useEffect(() => {
    handleSeachParametrizacao()
  }, [])

  useEffect(() => {
    handleSearch(watch())
  }, [page, rowsPerPage])


  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        padding={SPACING.spacing20}
        marginBottom={SPACING.spacing50}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <FilterTalkToUs
          control={control}
          handleSubmit={handleSubmit}
          handleOnSearch={handleSearch}
          areDatesValid={areDatesValid}
          handleOnClear={handleOnClear}
          parametrizationTalkToUs={parametrizationTalkToUs}
          errors={errors}
        // searchParams={searchParams}
        // handleOnChange={handleOnChange}
        />
        <TableTalkToUs
          tableData={talkToUsData}
          page={page}
          handleAnswerForm={handleAnswerForm}
          areDatesValid={areDatesValid}
          totalItems={pageCount}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Card>
    </>
  )
}

export default TalkToUsSearch
